import React, { useState } from "react";
import { useSearchParams } from "next/navigation";
import { Flex } from "@chakra-ui/react";
import { type IntegrationSimple } from "lib/sanity";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import { DemoFormWithContainer } from "components/demo-form";
import { StackedLogos } from "components/logos";
import { ProgressStepper } from "components/progress-stepper";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { type NextPage } from "next";

function getDynamicTitle(
  parameters: URLSearchParams,
  sources: IntegrationSimple[],
  destinations: IntegrationSimple[],
  defaultTitle: string,
) {
  // @TODO: Support X-Y pages, and integration pages when the header CTA is clicked

  // Destination page
  if (parameters.has("destination")) {
    try {
      const destinationName = decodeURI(parameters.get("destination") ?? "");
      if (destinations.some(({ name }) => name === destinationName)) {
        return `Talk with an expert to learn how to sync data to ${destinationName}`;
      }
    } catch {
      return defaultTitle;
    }
  }
  // Source page
  else if (parameters.has("source")) {
    try {
      const sourceName = decodeURI(parameters.get("source") ?? "");
      if (sources.some(({ name }) => name === sourceName)) {
        return `Talk with an expert to learn how to sync data from ${sourceName}`;
      }
    } catch {
      return defaultTitle;
    }
  }
  // General referring pages
  else if (parameters.has("ref")) {
    const referringPage = parameters.get("ref")!;

    switch (referringPage) {
      case "/platform/reverse-etl": {
        return "Book a demo to learn about Reverse ETL";
      }

      case "/platform/customer-studio": {
        return "Book a demo to learn about Hightouch Customer Studio";
      }

      case "/platform/match-booster": {
        return "Book a demo to learn about Hightouch Match Booster";
      }

      case "/platform/personalization-api": {
        return "Book a demo to learn about Hightouch Personalization API";
      }

      case "/platform/events": {
        return "Book a demo to learn about Hightouch Events";
      }

      case "/platform/identity-resolution": {
        return "Book a demo to learn about Identity Resolution";
      }

      case "/platform/campaign-intelligence": {
        return "Book a demo to learn about Campaign Intelligence";
      }

      case "/platform/ai-decisioning": {
        return "Book a demo to learn about AI Decisioning";
      }

      case "/platform/composable-cdp": {
        return "Book a demo to learn about Hightouch Composable CDP";
      }

      case "/platform/cdp":
      case "/cdp-consultation": {
        return "Book a demo to learn about Hightouch Composable CDP";
      }

      case "/pricing": {
        return "Contact sales";
      }

      default: {
        return defaultTitle;
      }
    }
  }

  // Default
  return defaultTitle;
}

const Demo: NextPage<LandingPageProps> = ({ page, sources, destinations, announcementBanner, defaultLogos }) => {
  const parameters = useSearchParams();
  const title = getDynamicTitle(parameters, sources, destinations, page.title);
  const logosToShow = page.customers?.length > 0 ? page.customers : defaultLogos;
  const [currentStep, setCurrentStep] = useState(1);

  return (
    <Page
      announcementBanner={announcementBanner}
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      footerOptions={{ hideCta: true }}
    >
      <Section my="0" pt={{ base: "big", md: "biggest", xl: "huge" }}>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: "bigger", lg: "biggest", xl: "huge" }}
          alignItems={{ base: "center", md: "start" }}
          w="100%"
        >
          <DemoFormWithContainer
            title={title}
            titleAs="h1"
            titleFontSize={{ base: "brand.lg", lg: "brand.xl" }}
            onSubmit={() => {
              setCurrentStep(2);
            }}
          />

          <ProgressStepper
            steps={[
              "Submit your email",
              "Schedule time directly with a Hightouch product expert",
              "Get a 30-minute product demo tailored to your use cases",
            ]}
            currentStep={currentStep}
            mt={{ md: "small", lg: "big" }}
            px={{ base: "bigger", md: 0 }}
            spacing={{ base: "bigger", md: "big" }}
            maxW={{ base: "lg", lg: "320px" }}
          />
        </Flex>
      </Section>

      <StackedLogos title="" customers={logosToShow} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export default Demo;

export const getStaticProps = getLandingPageProps("/demo", {
  loadDefaultLogos: true,
});
